import { ReactNode } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionDetails, AccordionSummary, SxProps } from '@mui/material';

import { Text } from '~/components/ui/text';
import { COLORS } from '~/styles/colors';

import { StyledAccordion, StyledArrowDropDownIcon, StyledChildSummaryContainer } from './styled';

type AccordionVariant = 'default' | 'child' | 'medium' | 'noBg' | 'draggableItem';

interface AccordionProps {
  summary: string | ReactNode;
  details: string | ReactNode;
  defaultExpanded?: boolean;
  className?: string;
  variant?: AccordionVariant;
  sx?: SxProps;
  summarySx?: SxProps;
  detailsSx?: SxProps;
  hideBottomBorder?: boolean;
}

export default function Accordion({
  summary,
  details,
  defaultExpanded,
  className = '',
  variant = 'default',
  sx = {},
  summarySx = {},
  detailsSx = {},
  hideBottomBorder = false,
}: AccordionProps): JSX.Element {
  const isChild = variant === 'child';
  const isMedium = variant === 'medium';
  const isNoBg = variant === 'noBg';
  const isDraggableItem = variant === 'draggableItem';
  const isSummaryString = typeof summary === 'string';
  const defaultSummarySx = isChild && !hideBottomBorder ? { '&:not(.Mui-expanded)': { marginBottom: '12px' } } : {};

  return (
    <StyledAccordion
      defaultExpanded={defaultExpanded}
      className={className}
      $isChild={isChild}
      sx={sx}
      $isMedium={isMedium}
      $isNoBg={isNoBg}
      $isDraggableItem={isDraggableItem}
      $hideBottomBorder={hideBottomBorder}
    >
      <AccordionSummary expandIcon={isChild ? null : <ExpandMoreIcon />} sx={{ ...defaultSummarySx, ...summarySx }}>
        {isSummaryString ? (
          <Text variant={isChild ? 'subheader' : 'h3'}>
            {isChild && <StyledArrowDropDownIcon />}
            {summary}
          </Text>
        ) : (
          <StyledChildSummaryContainer>
            {isChild && <StyledArrowDropDownIcon />}
            {summary}
          </StyledChildSummaryContainer>
        )}
      </AccordionSummary>
      <AccordionDetails sx={detailsSx}>
        {typeof details === 'string' ? (
          <Text variant="t16Regular" sx={{ color: COLORS.grey400 }}>
            {details}
          </Text>
        ) : (
          details
        )}
      </AccordionDetails>
    </StyledAccordion>
  );
}
